import { c as _c } from "react/compiler-runtime";
/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { eventFiles } from '@lexical/rich-text';
import { mergeRegister } from '@lexical/utils';
import { $getNearestNodeFromDOMNode, $getNodeByKey, $getRoot, COMMAND_PRIORITY_HIGH, COMMAND_PRIORITY_LOW, DRAGOVER_COMMAND, DROP_COMMAND, LexicalEditor } from 'lexical';
import * as React from 'react';
import { JSX, DragEvent as ReactDragEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Point, Rect, isHTMLElement } from './utils';
import { TbGripVertical } from 'react-icons/tb';
const SPACE = 4;
const TARGET_LINE_HALF_HEIGHT = 2;
const DRAGGABLE_BLOCK_MENU_CLASSNAME = 'draggable-block-menu';
const DRAG_DATA_FORMAT = 'application/x-lexical-drag-block';
const TEXT_BOX_HORIZONTAL_PADDING = 20;
const Downward = 1;
const Upward = -1;
const Indeterminate = 0;
let prevIndex = Infinity;
const getCurrentIndex = (keysLength: number): number => {
  if (keysLength === 0) return Infinity;
  if (prevIndex >= 0 && prevIndex < keysLength) return prevIndex;
  return Math.floor(keysLength / 2);
};
const getTopLevelNodeKeys = (editor: LexicalEditor): string[] => editor.getEditorState().read(() => $getRoot().getChildrenKeys());
const getCollapsedMargins = (elem: HTMLElement): {
  marginTop: number;
  marginBottom: number;
} => {
  const getMargin = (element: Element | null, margin: 'marginTop' | 'marginBottom'): number => element ? parseFloat(window.getComputedStyle(element)[margin]) : 0;
  const {
    marginTop,
    marginBottom
  } = window.getComputedStyle(elem);
  return {
    marginBottom: Math.max(parseFloat(marginBottom), getMargin(elem.nextElementSibling, 'marginTop')),
    marginTop: Math.max(parseFloat(marginTop), getMargin(elem.previousElementSibling, 'marginBottom'))
  };
};
const getBlockElement = (anchorElem: HTMLElement, editor: LexicalEditor, event: MouseEvent, useEdgeAsDefault = false): HTMLElement | null => {
  const anchorElementRect = anchorElem.getBoundingClientRect();
  const topLevelNodeKeys = getTopLevelNodeKeys(editor);
  let blockElem: HTMLElement | null = null;
  editor.getEditorState().read(() => {
    if (useEdgeAsDefault) {
      const [firstNode, lastNode] = [editor.getElementByKey(topLevelNodeKeys[0]!), editor.getElementByKey(topLevelNodeKeys[topLevelNodeKeys.length - 1]!)];
      const [firstNodeRect, lastNodeRect] = [firstNode?.getBoundingClientRect(), lastNode?.getBoundingClientRect()];
      if (firstNodeRect && lastNodeRect) {
        if (event.y < firstNodeRect.top) blockElem = firstNode;else if (event.y > lastNodeRect.bottom) blockElem = lastNode;
        if (blockElem) return;
      }
    }
    let index = getCurrentIndex(topLevelNodeKeys.length);
    let direction = Indeterminate;
    while (index >= 0 && index < topLevelNodeKeys.length) {
      const key = topLevelNodeKeys[index]!;
      const elem = editor.getElementByKey(key);
      if (elem === null) break;
      const point = new Point(event.x, event.y);
      const domRect = Rect.fromDOM(elem);
      const {
        marginTop,
        marginBottom
      } = getCollapsedMargins(elem);
      const rect = domRect.generateNewRect({
        bottom: domRect.bottom + marginBottom,
        left: anchorElementRect.left,
        right: anchorElementRect.right,
        top: domRect.top - marginTop
      });
      const {
        result,
        reason: {
          isOnTopSide,
          isOnBottomSide
        }
      } = rect.contains(point);
      if (result) {
        blockElem = elem;
        prevIndex = index;
        break;
      }
      if (direction === Indeterminate) {
        if (isOnTopSide) direction = Upward;else if (isOnBottomSide) direction = Downward;else direction = Infinity;
      }
      index += direction;
    }
  });
  return blockElem;
};
const isOnMenu = (element: HTMLElement) => !!element.closest(`.${DRAGGABLE_BLOCK_MENU_CLASSNAME}`);
const setMenuPosition = (targetElem: HTMLElement | null, floatingElem: HTMLElement, anchorElem: HTMLElement) => {
  if (!targetElem) {
    floatingElem.style.opacity = '0';
    floatingElem.style.transform = 'translate(-10000px, -10000px)';
    return;
  }
  const targetRect = targetElem.getBoundingClientRect();
  const targetStyle = window.getComputedStyle(targetElem);
  const floatingElemRect = floatingElem.getBoundingClientRect();
  const anchorElementRect = anchorElem.getBoundingClientRect();
  const top = targetRect.top + (parseInt(targetStyle.lineHeight, 10) - floatingElemRect.height) / 2 - anchorElementRect.top;
  const left = SPACE;
  floatingElem.style.opacity = '1';
  floatingElem.style.transform = `translate(${left}px, ${top}px)`;
};
const setDragImage = (dataTransfer: DataTransfer, draggableBlockElem: HTMLElement) => {
  const {
    transform
  } = draggableBlockElem.style;

  // Remove dragImage borders
  draggableBlockElem.style.transform = 'translateZ(0)';
  dataTransfer.setDragImage(draggableBlockElem, 0, 0);
  setTimeout(() => {
    draggableBlockElem.style.transform = transform;
  });
};
const setTargetLine = (targetLineElem: HTMLElement, targetBlockElem: HTMLElement, mouseY: number, anchorElem: HTMLElement) => {
  const {
    top: targetBlockElemTop,
    height: targetBlockElemHeight
  } = targetBlockElem.getBoundingClientRect();
  const {
    top: anchorTop,
    width: anchorWidth
  } = anchorElem.getBoundingClientRect();
  const {
    marginTop,
    marginBottom
  } = getCollapsedMargins(targetBlockElem);
  let lineTop = targetBlockElemTop;
  if (mouseY >= targetBlockElemTop) lineTop += targetBlockElemHeight + marginBottom / 2;else lineTop -= marginTop / 2;
  const top = lineTop - anchorTop - TARGET_LINE_HALF_HEIGHT;
  const left = TEXT_BOX_HORIZONTAL_PADDING - SPACE;
  targetLineElem.style.transform = `translate(${left}px, ${top}px)`;
  targetLineElem.style.width = `${anchorWidth - (TEXT_BOX_HORIZONTAL_PADDING - SPACE) * 2}px`;
  targetLineElem.style.opacity = '.4';
};
const hideTargetLine = (targetLineElem: HTMLElement | null) => {
  if (targetLineElem) {
    targetLineElem.style.opacity = '0';
    targetLineElem.style.transform = 'translate(-10000px, -10000px)';
  }
};
function useDraggableBlockMenu(editor, anchorElem, isEditable) {
  const $ = _c(31);
  const scrollerElem = anchorElem.parentElement;
  const menuRef = useRef(null);
  const targetLineRef = useRef(null);
  const isDraggingBlockRef = useRef(false);
  const [draggableBlockElem, setDraggableBlockElem] = useState(null);
  let t0;
  let t1;
  if ($[0] !== anchorElem || $[1] !== editor || $[2] !== scrollerElem) {
    t0 = () => {
      const onMouseMove = event => {
        const target = event.target;
        if (!isHTMLElement(target)) {
          setDraggableBlockElem(null);
          return;
        }
        if (isOnMenu(target)) {
          return;
        }
        const _draggableBlockElem = getBlockElement(anchorElem, editor, event);
        setDraggableBlockElem(_draggableBlockElem);
      };
      const onMouseLeave = () => {
        setDraggableBlockElem(null);
      };
      scrollerElem?.addEventListener("mousemove", onMouseMove);
      scrollerElem?.addEventListener("mouseleave", onMouseLeave);
      return () => {
        scrollerElem?.removeEventListener("mousemove", onMouseMove);
        scrollerElem?.removeEventListener("mouseleave", onMouseLeave);
      };
    };
    t1 = [scrollerElem, anchorElem, editor];
    $[0] = anchorElem;
    $[1] = editor;
    $[2] = scrollerElem;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[5] !== anchorElem || $[6] !== draggableBlockElem) {
    t2 = () => {
      if (menuRef.current) {
        setMenuPosition(draggableBlockElem, menuRef.current, anchorElem);
      }
      anchorElem.setAttribute("data-mark", " was here");
    };
    t3 = [anchorElem, draggableBlockElem];
    $[5] = anchorElem;
    $[6] = draggableBlockElem;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[9] !== anchorElem || $[10] !== editor) {
    t4 = () => {
      const onDragover = function onDragover(event_0) {
        if (!isDraggingBlockRef.current) {
          return false;
        }
        const [isFileTransfer] = eventFiles(event_0);
        if (isFileTransfer) {
          return false;
        }
        const {
          pageY,
          target: target_0
        } = event_0;
        if (!isHTMLElement(target_0)) {
          return false;
        }
        const targetBlockElem = getBlockElement(anchorElem, editor, event_0, true);
        const targetLineElem = targetLineRef.current;
        if (targetBlockElem === null || targetLineElem === null) {
          return false;
        }
        setTargetLine(targetLineElem, targetBlockElem, pageY, anchorElem);
        event_0.preventDefault();
        return true;
      };
      const onDrop = function onDrop(event_1) {
        if (!isDraggingBlockRef.current) {
          return false;
        }
        const [isFileTransfer_0] = eventFiles(event_1);
        if (isFileTransfer_0) {
          return false;
        }
        const {
          target: target_1,
          dataTransfer,
          pageY: pageY_0
        } = event_1;
        const dragData = dataTransfer?.getData(DRAG_DATA_FORMAT) || "";
        const draggedNode = $getNodeByKey(dragData);
        if (!draggedNode) {
          return false;
        }
        if (!isHTMLElement(target_1)) {
          return false;
        }
        const targetBlockElem_0 = getBlockElement(anchorElem, editor, event_1, true);
        if (!targetBlockElem_0) {
          return false;
        }
        const targetNode = $getNearestNodeFromDOMNode(targetBlockElem_0);
        if (!targetNode) {
          return false;
        }
        if (targetNode === draggedNode) {
          return true;
        }
        const targetBlockElemTop = targetBlockElem_0.getBoundingClientRect().top;
        if (pageY_0 >= targetBlockElemTop) {
          targetNode.insertAfter(draggedNode);
        } else {
          targetNode.insertBefore(draggedNode);
        }
        setDraggableBlockElem(null);
        return true;
      };
      return mergeRegister(editor.registerCommand(DRAGOVER_COMMAND, event_2 => onDragover(event_2), COMMAND_PRIORITY_LOW), editor.registerCommand(DROP_COMMAND, event_3 => onDrop(event_3), COMMAND_PRIORITY_HIGH));
    };
    t5 = [anchorElem, editor];
    $[9] = anchorElem;
    $[10] = editor;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] !== draggableBlockElem || $[14] !== editor) {
    t6 = function onDragStart(event_4) {
      const dataTransfer_0 = event_4.dataTransfer;
      if (!dataTransfer_0 || !draggableBlockElem) {
        return;
      }
      setDragImage(dataTransfer_0, draggableBlockElem);
      let nodeKey = "";
      editor.update(() => {
        const node = $getNearestNodeFromDOMNode(draggableBlockElem);
        if (node) {
          nodeKey = node.getKey();
        }
      });
      isDraggingBlockRef.current = true;
      dataTransfer_0.setData(DRAG_DATA_FORMAT, nodeKey);
    };
    $[13] = draggableBlockElem;
    $[14] = editor;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const onDragStart = t6;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = function onDragEnd() {
      isDraggingBlockRef.current = false;
      hideTargetLine(targetLineRef.current);
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const onDragEnd = t7;
  const t8 = isEditable ? undefined : "hidden";
  let t9;
  if ($[17] !== t8) {
    t9 = {
      visibility: t8
    };
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <TbGripVertical size={16} className="pointer-events-none " />;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t9) {
    t11 = <div style={t9} className="icon w-[16px] h-[16px] ">{t10}</div>;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== onDragStart || $[23] !== t11) {
    t12 = <div className="icon draggable-block-menu p-0 left-[-4px] top-0 absolute will-change-transform cursor-grab active:cursor-grabbing" ref={menuRef} draggable={true} onDragStart={onDragStart} onDragEnd={onDragEnd}>{t11}</div>;
    $[22] = onDragStart;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="draggable-block-target-line pointer-events-none bg-primary h-[4px] absolute left-0 top-0 opacity-0 will-change-transform" ref={targetLineRef} />;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12) {
    t14 = <>{t12}{t13}</>;
    $[26] = t12;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== anchorElem || $[29] !== t14) {
    t15 = createPortal(t14, anchorElem);
    $[28] = anchorElem;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}
export function DraggableBlockPlugin(t0) {
  const {
    anchorElem: t1
  } = t0;
  const anchorElem = t1 === undefined ? document.body : t1;
  const [editor] = useLexicalComposerContext();
  return useDraggableBlockMenu(editor, anchorElem, editor._editable);
}